import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { CountOutputData, Face } from "@utils/count-calculator/count-calculator.model";
import { MeshType } from "~/src/utils/shape-type";
import { Object3D, Intersection } from "three";
import { Mesh, ColorRepresentation, Vector3 } from "three";
import { MeshSelection } from "../../models/designer";
import { ColorsPicker } from "~/src/utils/color-constants";

@Injectable({
    providedIn: "root",
})
export class ComponentInteractionService {
    private interceptInfo = new Subject<Intersection<Object3D>[]>();
    private eventInfo = new Subject<Object3D>();
    private passSelectedMesh = new Subject<MeshType | null>();
    private cameraPosition = new Subject<Vector3>();
    private currentFacingDirectionSubject = new BehaviorSubject<Face>(Face.Front); // Default facing
    public currentFacingDirection$ = this.currentFacingDirectionSubject.asObservable();
    private selectedColor = new BehaviorSubject<string>(ColorsPicker[0].value);
    private outputPattern = new Subject<CountOutputData[]>();
    private redirectionPath = new Subject<string>();
    private prevoiusColor = new Subject<ColorRepresentation>();
    private invalidMeshes: Mesh[] = [];
    private validMeshes: Mesh[] = [];
    private meshSelectionsSubject = new BehaviorSubject<MeshSelection[]>([]);
    public meshSelections$ = this.meshSelectionsSubject.asObservable();

    constructor() {}
    private resetSubject = new Subject<void>();
    public reset$ = this.resetSubject.asObservable();

    public getSeletedShape(): Observable<MeshType | null> {
        return this.passSelectedMesh.asObservable();
    }

    public triggerReset(): void {
        this.resetSubject.next();
    }

    public setSelectedShape(selectedShape: MeshType | null) {
        this.passSelectedMesh.next(selectedShape);
    }

    public setGeneratedShapeInfo(event: Object3D): void {
        this.eventInfo.next(event);
    }

    public getGeneratedShapeInfo(): Observable<Object3D> {
        return this.eventInfo.asObservable();
    }

    public setInterceptInfo(interceptInfo: Intersection<Object3D>[]): void {
        interceptInfo && this.interceptInfo.next(interceptInfo);
    }

    public getInterceptInfo(): Observable<Intersection<Object3D>[]> {
        return this.interceptInfo.asObservable();
    }

    public setCameraPosition(position: Vector3) {
        this.cameraPosition.next(position);
    }

    public getCameraPosition(): Observable<Vector3> {
        return this.cameraPosition.asObservable();
    }

    public setCurrentFacingDirection(direction: Face): void {
        this.currentFacingDirectionSubject.next(direction);
    }

    public getCurrentFacingDirection(): Face {
        return this.currentFacingDirectionSubject.getValue();
    }

    public updateSelectedColor(color: string) {
        this.selectedColor.next(color);
    }

    public getSelectedColor(): Observable<string> {
        return this.selectedColor.asObservable();
    }

    public get meshSelections(): MeshSelection[] {
        return this.meshSelectionsSubject.value;
    }

    public setMeshSelections(meshSelections: MeshSelection[]) {
        this.meshSelectionsSubject.next(meshSelections);
    }

    public shapeColor(color: ColorRepresentation) {
        this.prevoiusColor.next(color);
    }

    public getShapeColor(): Observable<ColorRepresentation> {
        return this.prevoiusColor.asObservable();
    }

    public setOutputPattern(outputPattern: CountOutputData[]): void {
        this.outputPattern.next(outputPattern);
    }

    public getOutputPattern(): Observable<CountOutputData[]> {
        return this.outputPattern.asObservable();
    }

    public setRedirectionPath(redirectionPath: string): void {
        this.redirectionPath.next(redirectionPath);
    }

    public getRedirectionPath(): Observable<string> {
        return this.redirectionPath.asObservable();
    }

    public setInvalidMeshes(meshes: Mesh[]) {
        this.invalidMeshes = meshes;
    }

    public get getInvalidMeshes(): Mesh[] {
        return this.invalidMeshes;
    }

    public setValidMeshes(meshes: Mesh[]) {
        this.validMeshes = meshes;
    }
    public get getValidMeshes(): Mesh[] {
        return this.validMeshes;
    }
}
